import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import NewNotesContainer from "./AgendaItemForm.styles";
import { useBQMQ } from "context/BQMQ.context";
import { getAnalysts } from "../../../utils/bqmq.utils";
import { useSettings } from "context/Settings.context";
import Button from "components/Common/Button/Button";
import LoadingSpinner from "components/Common/LoadingSpinner/LoadingSpinner";
import Select from "components/Common/Select/Select";
import * as Database from "utils/database.utils";
import {
    AgendaItem,
    AgendaItemFile,
    TopicClassifier,
} from "../MorningMeetingDetails/MorningMeetingDetails.types";
import TextField from "../../../components/Common/TextField/TextField";
import MultipleFileUpload from "components/Common/MultipleFileUpload/MultipleFileUpload";
import FileDisplay from "../../../components/Common/FileDisplay/FileDisplay";
import Modal from "../../../components/Common/Modal/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft,
    faArrowRightArrowLeft,
    faCalendar,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { MorningMeeting } from "../MorningMeetings/MorningMeetings.types";
import {
    AgendaItemRequest,
    DownloadFileResponse,
    FileDeleteResponse,
    ServerResponseBody,
    UploadedFilesResponse,
} from "./AgendaItemForm.types";
import Tooltip from "@mui/material/Tooltip";
import { toDateAndTimeSeconds } from "../../../utils/general.utils";

const AGENDA_ITEM_ACCEPTED_FILES = [
    "application/pdf",
    "application/msword", // .doc
    "application/vnd.ms-powerpoint", // .ppt
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
    "image/png",
    "image/jpeg",
    "text/csv",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
    "application/vnd.ms-excel.sheet.macroEnabled.12", // .xlsm
].join(",");

const AgendaItemForm = () => {
    // @ts-ignore
    const { meetingId } = useParams();
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const history = useHistory();
    const [agendaItemId, setAgendaItemId] = useState<string | null>(
        urlParams.get("id")
    );

    const { companies } = useBQMQ();
    const [analysts, setAnalysts] = useState<any[]>([]);
    const [focusListCompanies, setFocusListCompanies] = useState<
        BQMQDropdownCompany[]
    >([]);
    const [nonFocusListCompanies, setNonFocusListCompanies] = useState<
        BQMQDropdownCompany[]
    >([]);
    const [meetingDate, setMeetingDate] = useState<string | undefined>(
        undefined
    );
    const [topicsOptions, setTopicsOptions] = useState<Option[]>([]);

    const [topic, setTopic] = useState<string>("");
    const [chosenCompanyId, setChosenCompanyId] = useState<string | null>(null);
    const [chosenSpeakerId, setChosenSpeakerId] = useState<string | null>(null);
    const [useCustomSpeaker, setUseCustomSpeaker] = useState<boolean>(false);
    const [customSpeakerName, setCustomSpeakerName] = useState<string>("");
    const [useCustomCompany, setUseCustomCompany] = useState<boolean>(false);
    const [customCompanyName, setCustomCompanyName] = useState<string>("");
    const [chosenTopicOptionId, setChosenTopicOptionId] = useState<
        string | null
    >(null);

    const [uploadedFiles, setUploadedFiles] = useState<AgendaItemFile[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

    const [loading, setLoading] = useState(false);
    const [reload, setReload] = useState(false);
    const { setShowLoadingToast, setToast } = useSettings();
    const [showCompanySelect, setShowCompanySelect] = useState(false);
    const [showPotentialCompanySelect, setShowPotentialCompanySelect] =
        useState(false);
    const [showFileDeleteModal, setShowFileDeleteModal] = useState(false);
    const [showAgendaDeleteModal, setShowAgendaDeleteModal] = useState(false);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [availableMeetings, setAvailableMeetings] = useState<
        MorningMeeting[]
    >([]);
    const [selectedTargetMeetingId, setSelectedTargetMeetingId] = useState<
        string | null
    >(null);
    const [loadingMeetings, setLoadingMeetings] = useState(false);
    const [movingItem, setMovingItem] = useState(false);

    const fetchMorningMeetingDate = async () => {
        const response = await Database.get(
            `api/bqmq_voting/morning-meetings/${meetingId}`
        );
        if (response.ok) {
            const data = await response.json();
            setMeetingDate(data?.data?.date);
        }
    };

    const fetchAgendaItem = async () => {
        setShowLoadingToast({ show: true, message: "LOADING" });

        const response = await Database.get(
            `api/bqmq_voting/morning-meetings/${meetingId}/agenda-items/${agendaItemId}`
        );

        if (response.ok) {
            const data: ServerResponseBody<AgendaItem> = await response.json();
            setTopic(data.data.topic);
            setUploadedFiles(data.data.files);
            setChosenSpeakerId(data.data.speaker);
            setChosenCompanyId(data.data.company?.id || null);
            setChosenTopicOptionId(data.data.topic_classifier.id);
            setReload(false);
        }

        setShowLoadingToast({ show: false, message: "" });
    };

    const fetchTopics = async () => {
        const response: Response = await Database.get(
            "api/bqmq_voting/morning-meetings/topics"
        );

        if (response.ok) {
            const data: ServerResponseBody<TopicClassifier[]> =
                await response.json();
            setTopicsOptions(
                data.data.map((topic: TopicClassifier) => ({
                    value: topic.id,
                    label: topic.value,
                }))
            );
        }
    };

    const isFormValid = (): boolean => {
        if (!chosenTopicOptionId || !topic) return false;

        // Either chosenSpeakerId or customSpeakerName must be provided
        if (!useCustomSpeaker && !chosenSpeakerId) return false;
        if (useCustomSpeaker && !customSpeakerName.trim()) return false;

        const chosenTopicClassifier = topicsOptions.find(
            (t) => t.value === chosenTopicOptionId
        )?.label;

        if (chosenTopicClassifier === "Focus list company" && !chosenCompanyId)
            return false;

        if (chosenTopicClassifier === "Potential focus list company") {
            if (useCustomCompany) {
                if (!customCompanyName.trim()) return false;
            } else {
                if (!chosenCompanyId) return false;
            }
        }

        return true;
    };

    const createSpeakerIfNeeded = async (): Promise<string | null> => {
        if (!useCustomSpeaker) {
            return chosenSpeakerId;
        }

        try {
            const analystResponse = await Database.post(
                "api/bqmq_voting/analysts",
                { name: customSpeakerName.trim() }
            );

            if (analystResponse.ok) {
                const analystData = await analystResponse.json();
                return analystData.data.id;
            } else {
                setToast({
                    show: true,
                    type: "error",
                    message: "Failed to create speaker. Please try again.",
                });
                return null;
            }
        } catch (error) {
            setToast({
                show: true,
                type: "error",
                message: "Failed to create speaker. Please try again.",
            });
            return null;
        }
    };

    const createCompanyIfNeeded = async (): Promise<{
        success: boolean;
        companyId: string | null;
    }> => {
        if (!useCustomCompany) {
            return { success: true, companyId: chosenCompanyId };
        }

        try {
            const companyResponse = await Database.post(
                "api/bqmq_voting/companies",
                { name: customCompanyName.trim() }
            );

            if (companyResponse.ok) {
                const companyData = await companyResponse.json();
                return { success: true, companyId: companyData.data.id };
            } else {
                setToast({
                    show: true,
                    type: "error",
                    message: "Failed to create company. Please try again.",
                });
                return { success: false, companyId: null };
            }
        } catch (error) {
            setToast({
                show: true,
                type: "error",
                message: "Failed to create company. Please try again.",
            });
            return { success: false, companyId: null };
        }
    };

    const submitAgendaItem = async () => {
        setShowLoadingToast({ show: true, message: "LOADING" });

        if (!isFormValid()) {
            setToast({
                show: true,
                type: "error",
                message: "Please ensure you have completed all fields.",
            });
            setShowLoadingToast({ show: false, message: "" });
            return;
        }

        const speakerId = await createSpeakerIfNeeded();
        if (!speakerId) {
            setShowLoadingToast({ show: false, message: "" });
            return;
        }

        const companyResult = await createCompanyIfNeeded();
        if (!companyResult.success) {
            setShowLoadingToast({ show: false, message: "" });
            return;
        }

        const payload: AgendaItemRequest = {
            id: agendaItemId || undefined,
            company_id: companyResult.companyId,
            speaker: speakerId!!,
            topic: topic,
            topic_classifier_id: chosenTopicOptionId!!,
        };

        const callMethod = payload.id ? Database.patch : Database.post;
        const successMessage = payload.id
            ? "Successfully edited agenda item"
            : "Successfully created agenda item";

        const response = await callMethod(
            `api/bqmq_voting/morning-meetings/${meetingId}/agenda-items`,
            payload
        );

        if (response.ok) {
            setToast({
                show: true,
                type: "success",
                message: successMessage,
            });
            setShowLoadingToast({ show: false, message: "" });
            const data: ServerResponseBody<AgendaItem> = await response.json();
            setAgendaItemId(data.data.id);
            setReload(true);
        } else {
            setShowLoadingToast({ show: false, message: "" });
        }
    };

    const deleteAgendaItem = async () => {
        setShowLoadingToast({ show: true, message: "LOADING" });

        const response = await Database.remove(
            `api/bqmq_voting/morning-meetings/${meetingId}/agenda-items/${agendaItemId}`
        );

        if (response.ok) {
            setToast({
                show: true,
                type: "success",
                message: "Agenda item deleted",
            });
            setShowLoadingToast({ show: false, message: "" });
            setShowAgendaDeleteModal(false);
            goBack();
        } else {
            setShowLoadingToast({ show: false, message: "" });
        }
    };

    const fetchAvailableMeetings = async () => {
        setLoadingMeetings(true);
        const response = await Database.get("api/bqmq_voting/morning-meetings");

        if (response.ok) {
            const data: ServerResponseBody<MorningMeeting[]> =
                await response.json();
            const filteredMeetings = data.data.filter(
                (meeting) =>
                    meeting.id !== meetingId &&
                    !meeting.cancelled &&
                    !meeting.deleted
            );
            setAvailableMeetings(filteredMeetings);
        }
        setLoadingMeetings(false);
    };

    const openMoveModal = async () => {
        setSelectedTargetMeetingId(null);
        setShowMoveModal(true);
        await fetchAvailableMeetings();
    };

    const moveAgendaItem = async () => {
        if (!selectedTargetMeetingId) {
            setToast({
                show: true,
                type: "error",
                message: "Please select a target meeting",
            });
            return;
        }

        setMovingItem(true);

        const response = await Database.post(
            `api/bqmq_voting/morning-meetings/${meetingId}/agenda-items/${agendaItemId}/move`,
            { target_meeting_id: selectedTargetMeetingId }
        );

        if (response.ok) {
            setToast({
                show: true,
                type: "success",
                message: "Agenda item moved successfully",
            });
            setMovingItem(false);
            setShowMoveModal(false);
            goBack();
        } else {
            const errorData = await response.json();
            setToast({
                show: true,
                type: "error",
                message: errorData?.description || "Failed to move agenda item",
            });
            setMovingItem(false);
        }
    };

    const handleFileUpload = async () => {
        setShowLoadingToast({ show: true, message: "Uploading Files" });

        if (selectedFiles.length === 0) {
            setToast({
                show: true,
                type: "error",
                message: "Please select files to upload",
            });
            setShowLoadingToast({ show: false, message: "" });
            return;
        }

        const formData = new FormData();
        selectedFiles.forEach((file) => formData.append(file.name, file));

        try {
            const response = await Database.postFile(
                `api/bqmq_voting/morning-meetings/${meetingId}/agenda-items/${agendaItemId}/files`,
                formData
            );
            const data: ServerResponseBody<UploadedFilesResponse> =
                await response.json();

            let success = false;
            const messages = data.data.uploaded_files.map((response) => {
                switch (response.code) {
                    case 200:
                        success = true;
                        return `Successfully uploaded ${response.file_name}`;
                    case 409:
                        return `File already exists: ${response.file_name}`;
                    default:
                        return `Failed to upload ${response.file_name}`;
                }
            });

            setToast({
                show: true,
                type: messages.every((m) => m.startsWith("Successfully"))
                    ? "success"
                    : "error",
                message: messages.join(". "),
            });

            if (success) {
                setShowLoadingToast({ show: false, message: "" });
                setSelectedFiles([]);
                setReload(true);
            }
        } catch (error) {
            setToast({
                show: true,
                type: "error",
                message:
                    "An error occurred while uploading files. Please try again later.",
            });
        } finally {
            setShowLoadingToast({ show: false, message: "" });
        }
    };

    const downloadFile = async (fileId: string) => {
        setShowLoadingToast({ show: true, message: "DOWNLOADING" });

        try {
            const response = await Database.get(
                `api/bqmq_voting/morning-meetings/${meetingId}/agenda-items/${agendaItemId}/files/${fileId}`
            );
            const data: ServerResponseBody<DownloadFileResponse> =
                await response.json();
            const link = document.createElement("a");
            link.href = data.data.uri;
            link.download = fileId;
            link.click();
        } catch (error) {
            setToast({
                show: true,
                message: "There was an error downloading the file.",
                type: "error",
            });
        } finally {
            setShowLoadingToast({ show: false, message: "" });
        }
    };

    const deleteFile = async (fileId: string) => {
        setShowLoadingToast({ show: true, message: `Deleting file...` });

        try {
            const response = await Database.patch(
                `api/bqmq_voting/morning-meetings/${meetingId}/agenda-items/${agendaItemId}/files/${fileId}/archive`
            );
            const data: ServerResponseBody<FileDeleteResponse> =
                await response.json();

            if (data.data.deleted === 1) {
                setShowFileDeleteModal(false);
                setShowLoadingToast({ show: false, message: "" });
                setToast({
                    show: true,
                    type: "success",
                    message: "File has been deleted.",
                });
                setReload(true);
            }
        } catch (error) {
            setToast({
                show: true,
                message: "There was an error deleting the file.",
                type: "error",
            });
        }

        setShowLoadingToast({ show: false, message: `` });
    };

    const goBack = () => {
        const newPath = location.pathname.replace("/agenda-item", "");
        history.push(newPath);
    };

    const onSpeakerRadioChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const isCustom = event.target.value === "custom";
        setUseCustomSpeaker(isCustom);
        if (isCustom) {
            setChosenSpeakerId(null);
        } else {
            setCustomSpeakerName("");
        }
    };

    const onCompanyRadioChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const isCustom = event.target.value === "custom";
        setUseCustomCompany(isCustom);
        if (isCustom) {
            setChosenCompanyId(null);
        } else {
            setCustomCompanyName("");
        }
    };

    // Initial data fetch
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            const initialDataCalls = [
                fetchTopics(),
                fetchMorningMeetingDate(),
                getAnalysts(setAnalysts, "all"),
            ];
            if (agendaItemId) {
                initialDataCalls.push(fetchAgendaItem());
            }

            await Promise.all(initialDataCalls);
            setLoading(false);
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (companies) {
            setFocusListCompanies(
                companies.filter(
                    (c: BQMQDropdownCompany) => c.focus_list_flag === 1
                )
            );
            setNonFocusListCompanies(
                companies.filter(
                    (c: BQMQDropdownCompany) => c.focus_list_flag !== 1
                )
            );
        }
    }, [companies]);

    useEffect(() => {
        if (reload) fetchAgendaItem();
    }, [reload]);

    // Speaker pre-fill for new agenda item
    useEffect(() => {
        // Do not continue with speaker pre-fill if editing agenda item or no company was selected
        if (!chosenCompanyId || agendaItemId) return;

        const company = focusListCompanies.find(
            (c: any) => c.value === chosenCompanyId
        );

        if (!company?.analyst_id) return;

        const activeAnalyst = analysts.find(
            (a: any) => a.value === company.analyst_id && a.active
        );

        if (activeAnalyst) {
            setChosenSpeakerId(activeAnalyst.value);
        }
    }, [chosenCompanyId, focusListCompanies, agendaItemId, analysts]);

    useEffect(() => {
        if (!showCompanySelect && !showPotentialCompanySelect) {
            setChosenCompanyId(null);
        }

        if (!showPotentialCompanySelect) {
            setUseCustomCompany(false);
            setCustomCompanyName("");
        }
    }, [showCompanySelect, showPotentialCompanySelect]);

    useEffect(() => {
        if (chosenTopicOptionId) {
            const chosenTopicClassifier = topicsOptions.find(
                (t) => t.value === chosenTopicOptionId
            )?.label;

            setShowCompanySelect(
                chosenTopicClassifier === "Focus list company"
            );

            setShowPotentialCompanySelect(
                chosenTopicClassifier === "Potential focus list company"
            );
        }
    }, [chosenTopicOptionId, topicsOptions]);

    return (
        <NewNotesContainer fileUploadActive={!!agendaItemId}>
            {showAgendaDeleteModal && (
                <Modal loading={false}>
                    <div className="modal">
                        <p>Are you sure you want to delete this agenda item?</p>
                        <div className="button-container">
                            <Button
                                color="secondary"
                                text="NO"
                                click={() => setShowAgendaDeleteModal(false)}
                            />
                            <Button
                                color="accent"
                                text="YES"
                                click={deleteAgendaItem}
                            />
                        </div>
                    </div>
                </Modal>
            )}
            {showMoveModal && (
                <Modal loading={movingItem}>
                    <div className="modal">
                        <p>Select the meeting to move this agenda item to:</p>
                        <div className="move-meeting-select">
                            {loadingMeetings ? (
                                <LoadingSpinner />
                            ) : (
                                <Select
                                    showAll={false}
                                    searchable
                                    filterName="Meeting"
                                    options={availableMeetings.map(
                                        (meeting) => ({
                                            value: meeting.id,
                                            label: `${meeting.title}`,
                                        })
                                    )}
                                    value={selectedTargetMeetingId}
                                    setValue={setSelectedTargetMeetingId}
                                />
                            )}
                        </div>
                        <div className="button-container">
                            <Button
                                color="secondary"
                                text="CANCEL"
                                click={() => setShowMoveModal(false)}
                                disabled={movingItem}
                            />
                            <Button
                                color="primary"
                                text="MOVE"
                                click={moveAgendaItem}
                                disabled={movingItem || loadingMeetings}
                            />
                        </div>
                    </div>
                </Modal>
            )}
            <div className="container">
                <div className="header">
                    <div className="title-container">
                        <h1 className="title">
                            {agendaItemId ? "EDIT" : "NEW"} AGENDA ITEM
                        </h1>
                        <div className="details">
                            <Tooltip title={<h2>MEETING DATE</h2>}>
                                <div className={"detail"}>
                                    <FontAwesomeIcon
                                        icon={faCalendar}
                                        fixedWidth
                                    />
                                    <p className={"capitalize"}>
                                        {toDateAndTimeSeconds(meetingDate)}
                                    </p>
                                </div>
                            </Tooltip>
                        </div>
                    </div>
                    <div className="actions-container">
                        <Button text="BACK" click={goBack} icon={faArrowLeft} />
                        {agendaItemId && (
                            <>
                                <Tooltip
                                    title={
                                        <h2>
                                            Move agenda item to another meeting
                                        </h2>
                                    }
                                >
                                    <div>
                                        <Button
                                            text="MOVE"
                                            click={openMoveModal}
                                            icon={faArrowRightArrowLeft}
                                        />
                                    </div>
                                </Tooltip>
                                <Button
                                    text="DELETE"
                                    color="accent"
                                    click={() => setShowAgendaDeleteModal(true)}
                                />
                            </>
                        )}
                    </div>
                </div>
                <hr />
                <div className="input-wrapper">
                    {loading ? (
                        <div className="loadingScreen">
                            <LoadingSpinner />
                        </div>
                    ) : (
                        <div className="form-wrapper">
                            <div className="input-wrapper">
                                <div className="selections">
                                    <div>
                                        <label>Topic classifier</label>
                                        <Select
                                            showAll={false}
                                            filterName="Topic"
                                            options={topicsOptions}
                                            value={chosenTopicOptionId}
                                            setValue={setChosenTopicOptionId}
                                        />
                                    </div>
                                    {showCompanySelect && (
                                        <div>
                                            <label>Company Select</label>
                                            <Select
                                                showAll={false}
                                                searchable
                                                filterName="Company"
                                                options={focusListCompanies}
                                                value={chosenCompanyId}
                                                setValue={setChosenCompanyId}
                                            />
                                        </div>
                                    )}
                                </div>
                                {showPotentialCompanySelect && (
                                    <div className="radio-group-wrapper">
                                        <label>Company Select</label>
                                        <RadioGroup
                                            value={
                                                useCustomCompany
                                                    ? "custom"
                                                    : "select"
                                            }
                                            onChange={onCompanyRadioChange}
                                            className="radio-group"
                                        >
                                            <FormControlLabel
                                                value="select"
                                                control={
                                                    <Radio
                                                        disableRipple
                                                        className="radio-option"
                                                    />
                                                }
                                                label="Select company"
                                            />
                                            <FormControlLabel
                                                value="custom"
                                                control={
                                                    <Radio
                                                        disableRipple
                                                        className="radio-option"
                                                    />
                                                }
                                                label="Enter Custom Company"
                                            />
                                        </RadioGroup>
                                        <div className="radio-text-input-wrapper">
                                            {useCustomCompany ? (
                                                <TextField
                                                    placeholder="Enter company name"
                                                    change={
                                                        setCustomCompanyName
                                                    }
                                                    value={customCompanyName}
                                                />
                                            ) : (
                                                <Select
                                                    showAll={false}
                                                    searchable
                                                    filterName="Company"
                                                    options={
                                                        nonFocusListCompanies
                                                    }
                                                    value={chosenCompanyId}
                                                    setValue={
                                                        setChosenCompanyId
                                                    }
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}
                                <div className="radio-group-wrapper">
                                    <label>Speaker</label>
                                    <RadioGroup
                                        value={
                                            useCustomSpeaker
                                                ? "custom"
                                                : "analyst"
                                        }
                                        onChange={onSpeakerRadioChange}
                                        className="radio-group"
                                    >
                                        <FormControlLabel
                                            value="analyst"
                                            control={
                                                <Radio
                                                    disableRipple
                                                    className="radio-option"
                                                />
                                            }
                                            label="Select Analyst"
                                        />
                                        <FormControlLabel
                                            value="custom"
                                            control={
                                                <Radio
                                                    disableRipple
                                                    className="radio-option"
                                                />
                                            }
                                            label="Enter Custom Speaker"
                                        />
                                    </RadioGroup>
                                    <div className="radio-text-input-wrapper">
                                        {useCustomSpeaker ? (
                                            <TextField
                                                placeholder="Enter speaker name"
                                                change={setCustomSpeakerName}
                                                value={customSpeakerName}
                                            />
                                        ) : (
                                            <Select
                                                showAll={false}
                                                searchable
                                                filterName="Analyst"
                                                options={analysts}
                                                value={chosenSpeakerId}
                                                setValue={setChosenSpeakerId}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="agenda-item-topic">
                                    <label>Topic</label>
                                    <TextField
                                        placeholder={"Agenda item topic"}
                                        change={setTopic}
                                        value={topic}
                                    />
                                </div>
                                <Button
                                    text="SUBMIT"
                                    click={submitAgendaItem}
                                    className="save-button"
                                />
                            </div>
                            {uploadedFiles.length > 0 && (
                                <div className="files">
                                    <label className="upload-files-label">
                                        Uploaded files
                                    </label>
                                    {uploadedFiles.map(({ name, id }) => (
                                        <div className="file-wrapper" key={id}>
                                            <FileDisplay
                                                value={name}
                                                onClick={() => downloadFile(id)}
                                            />
                                            {showFileDeleteModal && (
                                                <Modal loading={false}>
                                                    <div className="modal">
                                                        <p>
                                                            Are you sure you
                                                            want to delete this
                                                            file? <br />
                                                            <strong>
                                                                {name}
                                                            </strong>
                                                        </p>
                                                        <div className="button-container">
                                                            <Button
                                                                color="secondary"
                                                                text="NO"
                                                                click={() =>
                                                                    setShowFileDeleteModal(
                                                                        false
                                                                    )
                                                                }
                                                            />
                                                            <Button
                                                                color="accent"
                                                                text="YES"
                                                                click={() =>
                                                                    deleteFile(
                                                                        id
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </Modal>
                                            )}
                                            <button
                                                className="delete"
                                                onClick={() =>
                                                    setShowFileDeleteModal(true)
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={faTrash}
                                                />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="file-upload-wrapper">
                                <MultipleFileUpload
                                    selectedFiles={selectedFiles}
                                    setSelectedFiles={setSelectedFiles}
                                    accept={AGENDA_ITEM_ACCEPTED_FILES}
                                />
                                <Button
                                    text="UPLOAD FILES"
                                    click={handleFileUpload}
                                    className="save-button"
                                />
                                <p className="file-upload-warning">
                                    *File upload will be enabled once you submit
                                    a new note.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </NewNotesContainer>
    );
};

export default AgendaItemForm;
